import { Button, LayoutGridContainer, TextField } from '@madpaws/design-system';
import { Formik } from 'formik';
import React, { useState } from 'react';

import { trackEvent } from '~/components/analytics/analytics';
import { USER_CONTINUE_LOGIN_CLICK_EVENT_NAME } from '~/components/analytics/constants';
import { AuthApi } from '~/services/auth/authApi';

import styles from './AuthDialog.module.css';
import { emailValidationSchema } from './utils';
import { AuthMethods } from '../constants';
import { useAuthenticationContext } from '../useAuthenticationContext';

import type { ChangeEvent, ReactElement } from 'react';

type EmailSignUpFormValues = {
  email: string;
};

export const EmailVerificationForm = (): ReactElement => {
  const [formError, setFormError] = useState<string | null>(null);
  const formInitialValues = { email: '' };
  const {
    setEmail,
    setEmailLoginDialogVisible,
    setIsAuthDialogOpen,
    setIsEmailSignUpDialogOpen,
  } = useAuthenticationContext();

  const onSubmitForm = async (values: EmailSignUpFormValues): Promise<void> => {
    trackEvent(USER_CONTINUE_LOGIN_CLICK_EVENT_NAME, {
      email: values.email,
      grant_type: AuthMethods.PASSWORD,
    });

    const response = await AuthApi.validateEmail(values.email);

    if (response) {
      setIsAuthDialogOpen(false);
      setEmail(values.email);

      if (response.isTaken) {
        setEmailLoginDialogVisible(true);
      } else {
        setIsEmailSignUpDialogOpen(true);
      }
    }
  };

  return (
    <Formik
      initialValues={formInitialValues}
      onSubmit={onSubmitForm}
      validationSchema={emailValidationSchema}
    >
      {({ values, isSubmitting, isValid, setFieldValue, submitForm }): ReactElement => {
        const isSubmitButtonDisabled = isSubmitting || !isValid;

        const onTextFieldChange = (event: ChangeEvent<HTMLInputElement>): void => {
          const {
            target: { name, value },
          } = event;

          setFieldValue(name, value);
          // clear form error when user starts typing
          setFormError(null);
        };

        return (
          <div className={styles.form}>
            <LayoutGridContainer rowGap="default">
              <TextField
                isLabelVisuallyHidden
                label="Email"
                name="email"
                onChange={onTextFieldChange}
                value={values.email}
              />
            </LayoutGridContainer>
            <p className={styles.error}>{formError}</p>

            <div className={styles.buttonContainer}>
              <Button
                isDisabled={isSubmitButtonDisabled}
                isFullBleed
                label="Continue"
                onClick={submitForm}
                variant="secondary"
              />
            </div>
          </div>
        );
      }}
    </Formik>
  );
};
